import {
    Address,
    CartChangedError,
    Checkout,
    CheckoutSelectors,
    CheckoutService,
    CheckoutSettings,
    OrderRequestBody,
    PaymentMethod,
} from '@bigcommerce/checkout-sdk';
import { memoizeOne } from '@bigcommerce/memoize';
import { compact, find, get, isEmpty, noop } from 'lodash';
import React, { Component, ReactNode } from 'react';
import { connect } from 'react-redux'
import { ObjectSchema } from 'yup';

import { AnalyticsContextProps } from '@bigcommerce/checkout/analytics';
import { ErrorLogger } from '@bigcommerce/checkout/error-handling-utils';
import { withLanguage, WithLanguageProps } from '@bigcommerce/checkout/locale';
import { CheckoutContextProps, PaymentFormValues } from '@bigcommerce/checkout/payment-integration-api';
import { ChecklistSkeleton } from '@bigcommerce/checkout/ui';

import { withAnalytics } from '../analytics';
import B3Checkout, { B3MethodsProps } from '../b3checkout'
import store from '../b3checkout/store';
import { setGlobalIsPending, setGlobalLoadingStage } from '../b3checkout/store/actions';
import { constants, storage } from '../b3checkout/utils'
import { withCheckout } from '../checkout';
import {
    ErrorModal,
    ErrorModalOnCloseProps,
    isCartChangedError,
    isErrorWithType,
} from '../common/error';
import { EMPTY_ARRAY } from '../common/utility';
import { TermsConditionsType } from '../termsConditions';

import mapSubmitOrderErrorMessage, { mapSubmitOrderErrorTitle } from './mapSubmitOrderErrorMessage';
import mapToOrderRequestBody from './mapToOrderRequestBody';
import PaymentContext from './PaymentContext';
import PaymentForm from './PaymentForm';
import {
    getUniquePaymentMethodId,
    PaymentMethodId,
    PaymentMethodProviderType,
} from './paymentMethod';

export interface PaymentProps {
    errorLogger: ErrorLogger;
    isEmbedded?: boolean;
    isUsingMultiShipping?: boolean;
    checkEmbeddedSupport?(methodIds: string[]): void; // TODO: We're currently doing this check in multiple places, perhaps we should move it up so this check get be done in a single place instead.
    onCartChangedError?(error: CartChangedError): void;
    onFinalize?(): void;
    onFinalizeError?(error: Error): void;
    onReady?(): void;
    onSubmit?(): void;
    onSubmitError?(error: Error): void;
    onUnhandledError?(error: Error): void;
}

interface WithCheckoutPaymentProps {
    availableStoreCredit: number;
    cartUrl: string;
    defaultMethod?: PaymentMethod;
    finalizeOrderError?: Error;
    isInitializingPayment: boolean;
    isSubmittingOrder: boolean;
    isStoreCreditApplied: boolean;
    isTermsConditionsRequired: boolean;
    methods: PaymentMethod[];
    shouldExecuteSpamCheck: boolean;
    shouldLocaliseErrorMessages: boolean;
    submitOrderError?: Error;
    termsConditionsText?: string;
    termsConditionsUrl?: string;
    usableStoreCredit: number;
    applyStoreCredit(useStoreCredit: boolean): Promise<CheckoutSelectors>;
    clearError(error: Error): void;
    finalizeOrderIfNeeded(): Promise<CheckoutSelectors>;
    isPaymentDataRequired(): boolean;
    loadCheckout(): Promise<CheckoutSelectors>;
    loadPaymentMethods(): Promise<CheckoutSelectors>;
    submitOrder(values: OrderRequestBody): Promise<CheckoutSelectors>;
    storeHash: string;
    checkout: Checkout;
    billingAddress?: Address
    shippingAddress?: Address
    checkoutServiceSubscribe: CheckoutService['subscribe'];
}

interface PaymentState {
    didExceedSpamLimit: boolean;
    isReady: boolean;
    selectedMethod?: PaymentMethod;
    shouldDisableSubmit: { [key: string]: boolean };
    shouldHidePaymentSubmitButton: { [key: string]: boolean };
    submitFunctions: { [key: string]: ((values: PaymentFormValues) => void) | null };
    validationSchemas: { [key: string]: ObjectSchema<Partial<PaymentFormValues>> | null };
    formValues: PaymentFormValues,
}

interface B3Props {
    isB3User: boolean
    b3Methods: any
    checkoutSettings: any
}

class Payment extends Component<
    B3Props & PaymentProps & WithCheckoutPaymentProps & WithLanguageProps & AnalyticsContextProps,
    PaymentState
> {
    state: PaymentState = {
        didExceedSpamLimit: false,
        isReady: false,
        shouldDisableSubmit: {},
        shouldHidePaymentSubmitButton: {},
        validationSchemas: {},
        submitFunctions: {},
        formValues: { paymentProviderRadio: '' },
    };

    private grandTotalChangeUnsubscribe?: () => void;

    private getContextValue = memoizeOne(() => {
        return {
            disableSubmit: this.disableSubmit,
            setSubmit: this.setSubmit,
            setValidationSchema: this.setValidationSchema,
            hidePaymentSubmitButton: this.hidePaymentSubmitButton,
        };
    });

    get methods(): PaymentMethod[] {
        const {
            methods,
            isB3User,
            b3Methods,
        } = this.props

        const {
            paymentFilter
        } = B3Checkout.utils.util

        if (!isB3User) return methods.filter(method => method.id !== 'cheque')

        return paymentFilter(methods, b3Methods)
    }

    get isB2BExistingMethod() {
        const { defaultMethod } = this.props
        const isExistMethod = this.methods.some(({id}) => {(defaultMethod as any).id === id})

        return isExistMethod
    }

    async componentDidMount(): Promise<void> {
        const {
            finalizeOrderIfNeeded,
            onFinalize = noop,
            onFinalizeError = noop,
            onReady = noop,
            onUnhandledError = noop,
            checkout,
            isB3User,
            storeHash,
            methods,
            billingAddress,
            shippingAddress,
            usableStoreCredit,
            checkoutServiceSubscribe,
        } = this.props;


        if (usableStoreCredit) {
            this.handleStoreCreditChange(true);
        }

        try {
            store.dispatch(setGlobalIsPending(true))
            store.dispatch(setGlobalLoadingStage('common.loading_stages.payment_methods'))
            await this.loadPaymentMethodsOrThrow();

            await B3Checkout.payment.init()
        } catch (error) {
            onUnhandledError(error);
        } finally {
            store.dispatch(setGlobalIsPending(false))
        }

        try {
            const state = await finalizeOrderIfNeeded();
            const order = state.data.getOrder();
            const quoteId = storage.quoteCheckoutId.getValue() || ''

            if ((isB3User || quoteId) && checkout?.orderId)  {
                await B3Checkout.utils.payment.paymentsRefresh(methods)

                const isInvoice = await B3Checkout.payment.paySubmit(checkout, storeHash, checkout, billingAddress, shippingAddress)

                if (isInvoice) return
            }

            onFinalize(order?.orderId);
        } catch (error) {
            if (isErrorWithType(error) && error.type !== 'order_finalization_not_required') {
                onFinalizeError(error);
            }
        }

        this.grandTotalChangeUnsubscribe = checkoutServiceSubscribe(
            () => this.handleCartTotalChange(),
            ({ data }) => data.getCheckout()?.grandTotal,
            ({ data }) => data.getCheckout()?.outstandingBalance,
        );

        window.addEventListener('beforeunload', this.handleBeforeUnload);
        
        if (isB3User && !isEmpty(this.methods) && !this.isB2BExistingMethod) { 
            this.setSelectedMethod(this.methods[0])
        }

        this.setState({ isReady: true });
        onReady();
    }

    componentDidUpdate(): void {
        const {
            checkEmbeddedSupport = noop,
        } = this.props;

        checkEmbeddedSupport(this.methods.map(({ id }) => id));
    }

    componentWillUnmount(): void {
        if (this.grandTotalChangeUnsubscribe) {
            this.grandTotalChangeUnsubscribe();
            this.grandTotalChangeUnsubscribe = undefined;
        }

        window.removeEventListener('beforeunload', this.handleBeforeUnload);
    }

    render(): ReactNode {
        const {
            defaultMethod,
            isInitializingPayment,
            isUsingMultiShipping,
            ...rest
        } = this.props;

        const {
            didExceedSpamLimit,
            isReady,
            selectedMethod = defaultMethod,
            shouldDisableSubmit,
            validationSchemas,
            shouldHidePaymentSubmitButton,
        } = this.state;

        const uniqueSelectedMethodId =
            selectedMethod && getUniquePaymentMethodId(selectedMethod.id, selectedMethod.gateway);

        const resolveJsId = 'resolve-js'
        const resolveJsIdElement = document.getElementById(resolveJsId);
        const resolveJsSrc = 'https://app.resolvepay.com/js/resolve.js'
        
        if (!resolveJsIdElement) {
          const script = document.createElement('script');
          script.id = resolveJsId
          script.src = resolveJsSrc
          document.head.appendChild(script);
        }

        return (
            <PaymentContext.Provider value={this.getContextValue()}>
                <ChecklistSkeleton isLoading={!isReady}>
                    {!isEmpty(this.methods) && defaultMethod && (
                        <PaymentForm
                            {...rest}
                            defaultGatewayId={defaultMethod.gateway}
                            defaultMethodId={defaultMethod.id}
                            didExceedSpamLimit={didExceedSpamLimit}
                            isInitializingPayment={isInitializingPayment}
                            isUsingMultiShipping={isUsingMultiShipping}
                            methods={this.methods}
                            onMethodSelect={this.setSelectedMethod}
                            onStoreCreditChange={this.handleStoreCreditChange}
                            onSubmit={this.handleSubmit}
                            onUnhandledError={this.handleError}
                            selectedMethod={selectedMethod}
                            shouldDisableSubmit={
                                (uniqueSelectedMethodId &&
                                    shouldDisableSubmit[uniqueSelectedMethodId]) ||
                                undefined
                            }
                            shouldHidePaymentSubmitButton={
                                (uniqueSelectedMethodId &&
                                    rest.isPaymentDataRequired() &&
                                    shouldHidePaymentSubmitButton[uniqueSelectedMethodId]) ||
                                undefined
                            }
                            validationSchema={
                                (uniqueSelectedMethodId &&
                                    validationSchemas[uniqueSelectedMethodId]) ||
                                undefined
                            }
                        />
                    )}
                </ChecklistSkeleton>

                {this.renderOrderErrorModal()}
                {this.renderEmbeddedSupportErrorModal()}
            </PaymentContext.Provider>
        );
    }

    private renderOrderErrorModal(): ReactNode {
        const { finalizeOrderError, language, shouldLocaliseErrorMessages, submitOrderError } =
            this.props;

        // FIXME: Export correct TS interface
        const error: any = submitOrderError || finalizeOrderError;

        if (
            !error ||
            error.type === 'order_finalization_not_required' ||
            error.type === 'payment_cancelled' ||
            error.type === 'payment_invalid_form' ||
            error.type === 'spam_protection_not_completed' ||
            error.type === 'invalid_hosted_form_value'
        ) {
            return null;
        }

        return (
            <ErrorModal
                error={error}
                message={mapSubmitOrderErrorMessage(
                    error,
                    language.translate.bind(language),
                    shouldLocaliseErrorMessages,
                )}
                onClose={this.handleCloseModal}
                title={mapSubmitOrderErrorTitle(error, language.translate.bind(language))}
            />
        );
    }

    private renderEmbeddedSupportErrorModal(): ReactNode {
        const {
            checkEmbeddedSupport = noop,
        } = this.props;

        try {
            checkEmbeddedSupport(this.methods.map(({ id }) => id));
        } catch (error) {
            if (error instanceof Error) {
                return <ErrorModal error={error} onClose={this.handleCloseModal} />;
            }
        }

        return null;
    }

    private disableSubmit: (method: PaymentMethod, disabled?: boolean) => void = (
        method,
        disabled = true,
    ) => {
        const uniqueId = getUniquePaymentMethodId(method.id, method.gateway);
        const { shouldDisableSubmit } = this.state;

        if (shouldDisableSubmit[uniqueId] === disabled) {
            return;
        }

        this.setState({
            shouldDisableSubmit: {
                ...shouldDisableSubmit,
                [uniqueId]: disabled,
            },
        });
    };

    private hidePaymentSubmitButton: (method: PaymentMethod, disabled?: boolean) => void = (
        method,
        disabled = true,
    ) => {
        const uniqueId = getUniquePaymentMethodId(method.id, method.gateway);
        const { shouldHidePaymentSubmitButton } = this.state;

        if (shouldHidePaymentSubmitButton[uniqueId] === disabled) {
            return;
        }

        this.setState({
            shouldHidePaymentSubmitButton: {
                ...shouldHidePaymentSubmitButton,
                [uniqueId]: disabled,
            },
        });
    };

    // tslint:disable:cyclomatic-complexity
    private handleBeforeUnload: (event: BeforeUnloadEvent) => string | undefined = (event) => {
        const { defaultMethod, isSubmittingOrder, language } = this.props;
        const { selectedMethod = defaultMethod } = this.state;

        // TODO: Perhaps there is a better way to handle `adyen`, `afterpay`, `amazonpay`,
        // `checkout.com`, `converge`, `sagepay`, `stripev3` and `sezzle`. They require
        //  a redirection to another website during the payment flow but are not
        //  categorised as hosted payment methods.
        if (
            !isSubmittingOrder ||
            !selectedMethod ||
            selectedMethod.type === PaymentMethodProviderType.Hosted ||
            selectedMethod.type === PaymentMethodProviderType.PPSDK ||
            selectedMethod.gateway === PaymentMethodId.BlueSnapDirect ||
            selectedMethod.gateway === PaymentMethodId.BlueSnapV2 ||
            selectedMethod.id === PaymentMethodId.AmazonPay ||
            selectedMethod.id === PaymentMethodId.CBAMPGS ||
            selectedMethod.id === PaymentMethodId.Checkoutcom ||
            selectedMethod.id === PaymentMethodId.CheckoutcomGooglePay ||
            selectedMethod.id === PaymentMethodId.Converge ||
            selectedMethod.id === PaymentMethodId.Humm ||
            selectedMethod.id === PaymentMethodId.Laybuy ||
            selectedMethod.id === PaymentMethodId.Opy ||
            selectedMethod.id === PaymentMethodId.Quadpay ||
            selectedMethod.id === PaymentMethodId.SagePay ||
            selectedMethod.id === PaymentMethodId.Sezzle ||
            selectedMethod.id === PaymentMethodId.WorldpayAccess ||
            selectedMethod.id === PaymentMethodId.Zip ||
            selectedMethod.gateway === PaymentMethodId.AdyenV2 ||
            selectedMethod.gateway === PaymentMethodId.AdyenV2GooglePay ||
            selectedMethod.gateway === PaymentMethodId.AdyenV3 ||
            selectedMethod.gateway === PaymentMethodId.AdyenV3GooglePay ||
            selectedMethod.gateway === PaymentMethodId.Afterpay ||
            selectedMethod.gateway === PaymentMethodId.Clearpay ||
            selectedMethod.gateway === PaymentMethodId.Checkoutcom ||
            selectedMethod.gateway === PaymentMethodId.Mollie ||
            selectedMethod.gateway === PaymentMethodId.StripeV3
        ) {
            return;
        }

        const message = language.translate('common.leave_warning');

        event.returnValue = message;

        return message;
    };

    private handleCloseModal: (event: Event, props: ErrorModalOnCloseProps) => Promise<void> =
        async (_, { error }) => {
            if (!error) {
                return;
            }

            const { cartUrl, clearError, loadCheckout } = this.props;
            const { type: errorType } = error as any; // FIXME: Export correct TS interface

            if (
                errorType === 'provider_fatal_error' ||
                errorType === 'order_could_not_be_finalized_error'
            ) {
                window.location.replace(cartUrl || '/');
            }

            if (errorType === 'tax_provider_unavailable') {
                window.location.reload();
            }

            if (errorType === 'cart_consistency') {
                await loadCheckout();
            }

            if (isErrorWithType(error) && error.body) {
                const { body, headers, status } = error;

                if (body.type === 'provider_error' && headers.location) {
                    window.top?.location.assign(headers.location);
                }

                // Reload the checkout object to get the latest `shouldExecuteSpamCheck` value,
                // which will in turn make `SpamProtectionField` visible again.
                // NOTE: As a temporary fix, we're checking the status code instead of the error
                // type because of an issue with Nginx config, which causes the server to return
                // HTML page instead of JSON response when there is a 429 error.
                if (
                    status === 429 ||
                    body.type === 'spam_protection_expired' ||
                    body.type === 'spam_protection_failed'
                ) {
                    this.setState({ didExceedSpamLimit: true });

                    await loadCheckout();
                }
            }

            clearError(error);
        };

    private handleStoreCreditChange: (useStoreCredit: boolean) => void = async (useStoreCredit) => {
        const { applyStoreCredit, onUnhandledError = noop } = this.props;

        try {
            await applyStoreCredit(useStoreCredit);
        } catch (e) {
            onUnhandledError(e);
        }
    };

    private handleError: (error: Error) => void = (error: Error) => {
        const { onUnhandledError = noop, errorLogger } = this.props;

        const { type } = error as any;

        if (type === 'unexpected_detachment') {
            errorLogger.log(error);

            return;
        }

        return onUnhandledError(error);
    };

    private handleMessageFromIFrame: (data: any) => void = async (data) => {
      if (data) {
        if (data.eventType && data.eventType === 'close') {
          const resolveJsId = 'resolve-checkout'
          const resolveJsScriptElement = document.getElementById(resolveJsId);

          if (resolveJsScriptElement) {
            document.body.removeChild(resolveJsScriptElement);
          }

          return this.setState({ formValues: { paymentProviderRadio: '' } });
        }

        if (data.eventType && data.eventType === 'authorized') {
          const { submitOrder , isPaymentDataRequired, analyticsTracker, onSubmit = noop, isB3User, methods, storeHash, checkout, billingAddress, shippingAddress } = this.props;
          const { formValues } = this.state

          if (isB3User) await  B3Checkout.extrasFiles.validateOrderExtraFields(formValues)
          if (isB3User) await B3Checkout.utils.payment.paymentsRefresh(methods)
      
          // Third-party payment save order val
          B3Checkout.extrasFiles.saveOrderExtraVlaues(formValues)

          B3Checkout.extrasFiles.saveAddressExtrasFiles()

          const state = await submitOrder(mapToOrderRequestBody(formValues, isPaymentDataRequired()));
          const order = state.data.getOrder();

          const quoteId = storage.quoteCheckoutId.getValue() || ''

          if ((isB3User || quoteId) && order?.orderId)  {
              const isInvoice = await B3Checkout.payment.paySubmit(order, storeHash, checkout, billingAddress, shippingAddress)

              if (isInvoice) return
          }

          // new storefront
          if (storage.B3RoleId.getValue() === constants.B3Role.SALESREP && (window as any)?.B3?.checkout_super_clear_session === 'true') {
              sessionStorage.setItem('isReLogin', 'true')
          }

          analyticsTracker.paymentComplete();
          onSubmit(order?.orderId);
        }
        
      }
  };

    private handleSubmit: (values: PaymentFormValues) => void = async (values) => {
        const {
            defaultMethod,
            loadPaymentMethods,
            isPaymentDataRequired,
            onCartChangedError = noop,
            onSubmit = noop,
            onSubmitError = noop,
            submitOrder,
            analyticsTracker,
            isB3User,
            onUnhandledError = noop,
            storeHash,
            checkoutSettings,
            checkout,
            methods,
            billingAddress,
            shippingAddress,
            language,
        } = this.props;

        const { selectedMethod = defaultMethod, submitFunctions } = this.state;

        if (selectedMethod?.id) {
            const message = language.translate('payment.errors.no_select_payment_method');
            const isSelectPayment = this.methods.some((method) => method.id === selectedMethod.id)
            
            if (!isSelectPayment) return onUnhandledError(new Error(message))
        }

        analyticsTracker.clickPayButton({shouldCreateAccount: values.shouldCreateAccount});

        const customSubmit =
            selectedMethod &&
            submitFunctions[getUniquePaymentMethodId(selectedMethod.id, selectedMethod.gateway)];

        if (customSubmit) {
            return customSubmit(values);
        }

        const {
            checkoutPaymentAdditionalEnableExtra,
            checkoutPaymentAdditionalExtraFieldsRequired,
            checkoutPaymentAdditionalExtraFields,
            checkoutPaymentPurchaseEnableExtra,
            checkoutPaymentPurchaseExtraFields,
            checkoutPaymentPurchaseExtraFieldsRequired,
        } = checkoutSettings

        const b3PoNumberRequired = () => {
            return isB3User &&
            selectedMethod?.id === 'cheque' &&
            +checkoutPaymentPurchaseEnableExtra.value === 1 &&
            +checkoutPaymentPurchaseExtraFieldsRequired.value === 1 &&
            +checkout?.grandTotal !== 0
        }

        const {
            storage: {
                B3PoNumber,
                B3PaymentsAdditionalTextFieldValue,
            },
        } = B3Checkout.utils

        const isVerified = await B3Checkout.customer.salesRepVerify()

        if (!isVerified) return

        const requiredFieldTip = (fieldName: string) => {
            return new Error(`${B3Checkout.locales.tips['b3.field.required']} ${fieldName}`)
        }

        if (storage.IPCheckout.getValue() !== '1' && b3PoNumberRequired() && !B3PoNumber.getValue()) return onUnhandledError(requiredFieldTip(checkoutPaymentPurchaseExtraFields.value))

        const b3PaymentsAdditionalTextFieldRequired = () => {
            return isB3User &&
                +checkoutPaymentAdditionalEnableExtra.value === 1 &&
                +checkoutPaymentAdditionalExtraFieldsRequired.value === 1
        }

        if (storage.IPCheckout.getValue() !== '1' && b3PaymentsAdditionalTextFieldRequired() && !B3PaymentsAdditionalTextFieldValue.getValue()) return onUnhandledError(requiredFieldTip(checkoutPaymentAdditionalExtraFields.value))
        
        try {
          if (selectedMethod?.id === 'cod') {
            this.setState({ formValues: values });

            // Listen to message from iframe window
            window.addEventListener('message',(e) => {
              return this.handleMessageFromIFrame(e.data);
            }, false);

            const items: { name: string; quantity: number; unit_price: number; sku: string; }[] = []
            
            checkout.cart?.lineItems?.physicalItems.forEach((item) => {
              items.push({name: item.name, quantity: item.quantity, unit_price: item.salePrice, sku: item.sku})
            });

            const resolveJsId = 'resolve-checkout'
            const script = document.createElement('script');
            const checkoutMode = !process.env.REACT_APP_RESOLVE_ENV || process.env.REACT_APP_RESOLVE_ENV === 'development' ? 'local' : process.env.REACT_APP_RESOLVE_ENV;
            const resolveAppClientId = process.env.REACT_APP_RESOLVE_APP_CLIENT_ID || null;

            script.id = resolveJsId;

            const options = {
              method: 'GET',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
            };
            
            let currentCustomerJWT = null;

            try {
              if (resolveAppClientId) {
                const response = await fetch(
                  `/customer/current.jwt?app_client_id=${resolveAppClientId}`,
                  options
                );
            
                const raw = await response.text(); // response as raw text
                const data = JSON.parse(raw);      // parse it to an object
                
                currentCustomerJWT = get(data, 'token', null);  // extract the token string
              }
            } catch (error) {
              console.error('Error fetching current customer JWT', error);
            }
            
            const scriptString =
              `resolve.checkout({` +
                `
                  ${checkoutMode}: true, 
                  modal: true,
                  merchant: {
                    storeHash:  '${storeHash}',
                  },
                  customer: {
                    first_name: '${billingAddress?.firstName}',
                    last_name:  '${billingAddress?.lastName}',
                    name:       '${`${billingAddress?.firstName} ${billingAddress?.lastName}`}', 
                    phone:      '${billingAddress?.phone}',
                    email:      '${checkout.billingAddress?.email}',
                  },
                  shipping: {
                    name:            '${`${shippingAddress?.firstName} ${shippingAddress?.lastName}`}',
                    company_name:    '${shippingAddress?.company}', 
                    phone:           '${shippingAddress?.phone}',
                    address_line1:   '${shippingAddress?.address1}',
                    address_line2:   '${shippingAddress?.address2}', 
                    address_city:    '${shippingAddress?.city}',
                    address_state:   '${shippingAddress?.stateOrProvince}',
                    address_postal:  '${shippingAddress?.postalCode}',
                    address_country: '${shippingAddress?.country}',
                  },
                  billing: {
                    name:            '${`${checkout.billingAddress?.firstName} ${checkout.billingAddress?.lastName}`}',
                    company_name:    '${billingAddress?.company}',
                    phone:           '${billingAddress?.phone}',
                    address_line1:   '${billingAddress?.address1}',
                    address_line2:   '${billingAddress?.address2}',
                    address_city:    '${billingAddress?.city}',
                    address_state:   '${billingAddress?.stateOrProvince}',
                    address_postal:  '${billingAddress?.postalCode}',
                    address_country: '${billingAddress?.country}',
                  },
                  items:            ${JSON.stringify(items)},
                  order_number:     ${null}, 
                  po_number:        ${null}, 
                  shipping_amount:  ${checkout.shippingCostTotal},
                  tax_amount:       ${checkout.taxTotal},
                  total_amount:     ${checkout.grandTotal},
                  cart_id:          '${checkout.id}',
                  metadata: {
                    platform_type: 'bigcommerce',
                  },
                  token: ${currentCustomerJWT ? `'${currentCustomerJWT}'` : null}
                ` +
              `});`;
    
            script.innerHTML = scriptString
            document.body.appendChild(script);

          } else {
            if (isB3User) await  B3Checkout.extrasFiles.validateOrderExtraFields(values)
            if (isB3User) await B3Checkout.utils.payment.paymentsRefresh(methods)
        
            // Third-party payment save order val
            B3Checkout.extrasFiles.saveOrderExtraVlaues(values)

            B3Checkout.extrasFiles.saveAddressExtrasFiles()

            const state = await submitOrder(mapToOrderRequestBody(values, isPaymentDataRequired()));
            const order = state.data.getOrder();

            const quoteId = storage.quoteCheckoutId.getValue() || ''

            if ((isB3User || quoteId) && order?.orderId)  {
                const isInvoice = await B3Checkout.payment.paySubmit(order, storeHash, checkout, billingAddress, shippingAddress)

                if (isInvoice) return
            }

            // new storefront
            if (storage.B3RoleId.getValue() === constants.B3Role.SALESREP && (window as any)?.B3?.checkout_super_clear_session === 'true') {
                sessionStorage.setItem('isReLogin', 'true')
            }

            analyticsTracker.paymentComplete();

            onSubmit(order?.orderId);
          }
              
        } catch (error) {
          analyticsTracker.paymentRejected();

          if (isErrorWithType(error) && error.type === 'payment_method_invalid') {
              return loadPaymentMethods();
          }

          if (isCartChangedError(error)) {
              return onCartChangedError(error);
          }

          onSubmitError(error);
        }
      };

    private setSelectedMethod: (method?: PaymentMethod) => void = (method) => {
        const { selectedMethod } = this.state;

        if (selectedMethod === method) {
            return;
        }

        if (method) {
            this.trackSelectedPaymentMethod(method);
        }

        this.setState({ selectedMethod: method });
    };

    private setSubmit: (
        method: PaymentMethod,
        fn: (values: PaymentFormValues) => void | null,
    ) => void = (method, fn) => {
        const uniqueId = getUniquePaymentMethodId(method.id, method.gateway);
        const { submitFunctions } = this.state;

        if (submitFunctions[uniqueId] === fn) {
            return;
        }

        this.setState({
            submitFunctions: {
                ...submitFunctions,
                [uniqueId]: fn,
            },
        });
    };

    private setValidationSchema: (
        method: PaymentMethod,
        schema: ObjectSchema<Partial<PaymentFormValues>> | null,
    ) => void = (method, schema) => {
        const uniqueId = getUniquePaymentMethodId(method.id, method.gateway);
        const { validationSchemas } = this.state;

        if (validationSchemas[uniqueId] === schema) {
            return;
        }

        this.setState({
            validationSchemas: {
                ...validationSchemas,
                [uniqueId]: schema,
            },
        });
    };

    private trackSelectedPaymentMethod(method: PaymentMethod) {
        const { analyticsTracker } = this.props;

        const methodName = method.config.displayName || method.id;
        const methodId = method.id;

        analyticsTracker.selectedPaymentMethod(methodName, methodId);
    }

    private async loadPaymentMethodsOrThrow(): Promise<void> {
        const {
            loadPaymentMethods,
            onUnhandledError = noop,
        } = this.props;

        try {
            await loadPaymentMethods();

            const selectedMethod = this.state.selectedMethod || this.props.defaultMethod;

            if (selectedMethod) {
                this.trackSelectedPaymentMethod(selectedMethod);
            }
        } catch (error) {
            onUnhandledError(error);
        }
    }

    private async handleCartTotalChange(): Promise<void> {
        const { isReady } = this.state;

        if (!isReady) {
            return;
        }

        this.setState({ isReady: false });

        await this.loadPaymentMethodsOrThrow();

        this.setState({ isReady: true });
    }
}

export function mapToPaymentProps({
        checkoutService,
        checkoutState,
}: CheckoutContextProps, { isB3User, b3Methods }: { isB3User: boolean, b3Methods: B3MethodsProps[] }): WithCheckoutPaymentProps | null {
    const {
        data: {
            getCheckout,
            getConfig,
            getCustomer,
            getConsignments,
            getBillingAddress,
            getShippingAddress,
            getOrder,
            getPaymentMethod,
            getPaymentMethods,
            isPaymentDataRequired,
            getPaymentProviderCustomer,
        },
        errors: { getFinalizeOrderError, getSubmitOrderError },
        statuses: { isInitializingPayment, isSubmittingOrder },
    } = checkoutState;
    const {
        paymentFilter
    } = B3Checkout.utils.util

    const checkout = getCheckout();
    const config = getConfig();
    const customer = getCustomer();
    const consignments = getConsignments();
    const paymentProviderCustomer = getPaymentProviderCustomer();

    const { isComplete = false } = getOrder() || {};
    let methods = getPaymentMethods() || EMPTY_ARRAY;

    // TODO: In accordance with the checkout team, this functionality is temporary and will be implemented in the backend instead.
    if (paymentProviderCustomer?.stripeLinkAuthenticationState) {
        const stripeUpePaymentMethod = methods.filter(method =>
            method.id === 'card' && method.gateway === PaymentMethodId.StripeUPE
        );

        methods = stripeUpePaymentMethod.length ? stripeUpePaymentMethod : methods;
    }

    if (!checkout || !config || !customer || isComplete) {
        return null;
    }

    const {
        enableTermsAndConditions: isTermsConditionsEnabled,
        features,
        orderTermsAndConditionsType: termsConditionsType,
        orderTermsAndConditions: termsCondtitionsText,
        orderTermsAndConditionsLink: termsCondtitionsUrl,
    } = config.checkoutSettings as CheckoutSettings & { orderTermsAndConditionsLocation: string };

    const {
        storeHash,
    } = config.storeProfile

    const isTermsConditionsRequired = isTermsConditionsEnabled;
    const selectedPayment = find(checkout.payments, {
        providerType: PaymentMethodProviderType.Hosted,
    });

    const { isStoreCreditApplied } = checkout;

    let selectedPaymentMethod;
    let filteredMethods;

    filteredMethods = methods.filter((method: PaymentMethod) => {
        if (method.id === PaymentMethodId.Bolt && method.initializationData) {
            return !!method.initializationData.showInCheckout;
        }

        return true;
    });

    if (consignments && consignments.length > 1) {
        const multiShippingIncompatibleMethodIds: string[] = [
            PaymentMethodId.AmazonPay,
        ];

        filteredMethods = methods.filter((method: PaymentMethod) => {
            return multiShippingIncompatibleMethodIds.indexOf(method.id) === -1;
        });
    }

    if (selectedPayment) {
        selectedPaymentMethod = getPaymentMethod(
            selectedPayment.providerId,
            selectedPayment.gatewayId,
        );
        filteredMethods = selectedPaymentMethod
            ? compact([selectedPaymentMethod])
            : filteredMethods;
    } else {
        selectedPaymentMethod = find(filteredMethods, {
            config: { hasDefaultStoredInstrument: true },
        });
        // eslint-disable-next-line no-self-assign
        filteredMethods = filteredMethods;

        const newMethods = paymentFilter(methods, b3Methods)
        if (isB3User && newMethods.length > 0) {
            selectedPaymentMethod = newMethods[0]
        }
    }

    const shippingAddress = consignments && consignments.length > 1 ? undefined : getShippingAddress();

    return {
        applyStoreCredit: checkoutService.applyStoreCredit,
        availableStoreCredit: customer.storeCredit,
        billingAddress: getBillingAddress(),
        shippingAddress,
        cartUrl: config.links.cartLink,
        clearError: checkoutService.clearError,
        defaultMethod: selectedPaymentMethod || filteredMethods[0],
        finalizeOrderError: getFinalizeOrderError(),
        finalizeOrderIfNeeded: checkoutService.finalizeOrderIfNeeded,
        loadCheckout: checkoutService.loadCheckout,
        isInitializingPayment: isInitializingPayment(),
        isPaymentDataRequired,
        isStoreCreditApplied,
        isSubmittingOrder: isSubmittingOrder(),
        isTermsConditionsRequired,
        loadPaymentMethods: checkoutService.loadPaymentMethods,
        methods: filteredMethods,
        shouldExecuteSpamCheck: checkout.shouldExecuteSpamCheck,
        shouldLocaliseErrorMessages:
            features['PAYMENTS-6799.localise_checkout_payment_error_messages'],
        submitOrder: checkoutService.submitOrder,
        submitOrderError: getSubmitOrderError(),
        checkoutServiceSubscribe: checkoutService.subscribe,
        termsConditionsText:
            isTermsConditionsRequired && termsConditionsType === TermsConditionsType.TextArea
                ? termsCondtitionsText
                : undefined,
        termsConditionsUrl:
            isTermsConditionsRequired && termsConditionsType === TermsConditionsType.Link
                ? termsCondtitionsUrl
                : undefined,
        usableStoreCredit:
            checkout.grandTotal > 0 ? Math.min(checkout.grandTotal, customer.storeCredit || 0) : 0,
        storeHash,
        checkout,
    };
}

const mapStateToProps = (state: {
    payment: { methods: B3MethodsProps[]  }
    global: { isB3User: boolean, checkoutSettings: any }
}) => {
    const {
        global: {
            isB3User,
            checkoutSettings,
        },
        payment: {
            methods: b3Methods
        },
    } = state

    return {
        isB3User,
        b3Methods,
        checkoutSettings,
    }
}

export default connect(mapStateToProps)(withAnalytics(withLanguage(withCheckout(mapToPaymentProps)(Payment))));
